// One finding as a record row: its reading (title, verdict, value against
// its target, actions, details) beside the chart that shows where it stands.
import { Disclosure, VerdictBadge, VizFigure, VizSplit } from "@dashboard-intelligent/ui";
import type { Insight } from "@dashboard-intelligent/api/src/modules/industry/types";
import { formatMetric } from "../../format";
import { tenantPath } from "../../tenant";
import { isGraded } from "../shared/metric";
import { VisualChart, tidyTitle, visualFigure } from "../shared/visual";
import { thresholdText, type FindingPlan } from "./model";

export interface FindingProps {
  plan: FindingPlan;
  /** Action titles by id, to name each link; an unknown id reads as a numbered action. */
  actionTitles?: Map<string, string>;
  /** The stage's label, from the overview; the stage id when it is not known. */
  stageLabel: string;
}

export function Finding({ plan, stageLabel, actionTitles = new Map() }: FindingProps) {
  const { insight, visual, mark, target } = plan;
  const { metric } = insight;
  const value = formatMetric(metric.value, metric.format);
  const graded = isGraded(metric) ? metric : null;
  const targetLine = graded ? `Target ${formatMetric(graded.target.target, metric.format)}. ` : "";
  const thresholds = thresholdText(insight);
  const aside = (
    <>
      <div className="viz-finding-head">
        <h3 className="viz-finding-title">{insight.title}</h3>
        <VerdictBadge verdict={insight.verdict} />
      </div>
      {metric.value === null ? (
        <p className="viz-muted">{metric.note ?? "Data belum bisa menghasilkan nilai ini."}</p>
      ) : (
        <VizFigure value={value} label={metric.label} />
      )}
      {thresholds ? <p className="viz-muted">{`${targetLine}${thresholds}. Nilai ini rata-rata seluruh periode.`}</p> : null}
      <ActionLinks insight={insight} titles={actionTitles} />
      <Disclosure title="Rincian">
        <p className="viz-detail-text">{insight.finding}</p>
        <dl className="viz-facts">
          <dt>Aturan</dt>
          <dd>{insight.basis}</dd>
          <dt>Tahap</dt>
          <dd>{stageLabel}</dd>
          <dt>Owner</dt>
          <dd>{insight.owner}</dd>
          <dt>Rumus</dt>
          <dd>{metric.formula}</dd>
          <dt>Sumber</dt>
          <dd>{metric.sources.length > 0 ? metric.sources.join(", ") : "Tidak dicatat"}</dd>
        </dl>
      </Disclosure>
    </>
  );
  const chart =
    visual && mark ? (
      <>
        <div className="viz-finding-chart-head">
          <p className="viz-finding-chart-title">{plan.source === "evidence" ? `Bukti: ${tidyTitle(visual.title)}` : tidyTitle(visual.title)}</p>
          {visualFigure(visual, mark)}
        </div>
        <VisualChart visual={visual} mark={mark} target={target ?? undefined} limit={8} />
      </>
    ) : undefined;
  return <VizSplit id={insight.id} aside={aside} visual={chart} />;
}

function ActionLinks({ insight, titles }: { insight: Insight; titles: Map<string, string> }) {
  const ids = insight.actionIds;
  if (ids.length === 0) return <p className="viz-muted">Belum ada tindakan untuk temuan ini.</p>;
  return (
    <ul className="viz-links" aria-label={`Tindakan untuk ${insight.title}`}>
      {ids.map((id, i) => (
        <li key={id}>
          <a className="viz-link" href={`${tenantPath("/action")}#${encodeURIComponent(id)}`}>
            {titles.get(id) ?? `Tindakan ${i + 1} dari ${ids.length}`}
          </a>
        </li>
      ))}
    </ul>
  );
}
